<template>
    <div class="route-map-container">
        <v-card outlined>
            <v-toolbar flat dense color="green darken-1" dark>
                <v-toolbar-title>{{ title || "Visualización de Ruta" }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-switch v-model="showRealRoute" inset hide-details class="mr-4" label="Ruta por carretera"
                    @change="toggleRouteType" />
                <v-btn icon @click="$emit('close')">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text>
                <v-alert v-if="validStops.length < 2" type="warning" text dense>
                    No hay suficientes paradas con coordenadas válidas para mostrar la ruta.
                    Debe haber al menos 2 paradas con coordenadas válidas.
                </v-alert>

                <div v-if="isLoading" class="d-flex justify-center align-center" style="height: 450px">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                </div>

                <div v-else class="route-map" style="height: 450px">
                    <l-map ref="map" :zoom="zoom" :center="center" @ready="onMapReady" :options="mapOptions">
                        <l-tile-layer :url="tileUrl" :attribution="attribution" />

                        <!-- Marcadores para las paradas -->
                        <l-marker v-for="(stop, index) in validStops" :key="'marker-' + stop.id"
                            :lat-lng="[parseFloat(stop.latitude), parseFloat(stop.longitude)]"
                            :icon="getStopIcon(index)">
                            <l-tooltip :options="{ permanent: false, direction: 'top' }">
                                <div>
                                    <strong>{{ index + 1 }}. {{ stop.name }}</strong>
                                    <div v-if="stop.time">Hora: {{ stop.time }}</div>
                                </div>
                            </l-tooltip>
                        </l-marker>

                        <!-- Línea recta entre paradas (cuando no se muestra ruta real) -->
                        <l-polyline v-if="!showRealRoute && validStops.length >= 2"
                            :lat-lngs="validStops.map(s => [parseFloat(s.latitude), parseFloat(s.longitude)])"
                            color="blue" :weight="4" :opacity="0.7" :dash-array="'10, 10'"></l-polyline>

                        <!-- Capa para la ruta por carretera -->
                        <l-polyline v-if="showRealRoute && routeCoordinates.length > 0"
                            :lat-lngs="routeCoordinates"
                            color="#1976D2" :weight="5" :opacity="0.7"></l-polyline>
                    </l-map>
                </div>

                <v-card flat outlined class="mt-2 pa-2">
                    <div class="d-flex align-center flex-wrap">
                        <v-chip small color="primary" class="mr-2 mb-1">
                            {{ validStops.length }} paradas válidas
                        </v-chip>
                        <v-chip small color="green" class="mr-2 mb-1" v-if="validStops.length > 0">
                            Inicio: {{ validStops[0].name }}
                        </v-chip>
                        <v-chip small color="red" class="mr-2 mb-1" v-if="validStops.length > 1">
                            Fin: {{ validStops[validStops.length - 1].name }}
                        </v-chip>
                        <v-chip small :color="showRealRoute ? 'blue' : 'grey'" class="mr-2 mb-1">
                            {{ showRealRoute ? 'Ruta por carretera' : 'Línea directa' }}
                        </v-chip>
                        <v-chip small color="orange" class="mr-2 mb-1" v-if="routeDistance">
                            Distancia: {{ routeDistance }} km
                        </v-chip>
                        <v-chip small color="purple" class="mr-2 mb-1" v-if="routeDuration">
                            Tiempo: {{ routeDuration }}
                        </v-chip>
                    </div>
                </v-card>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { LMap, LTileLayer, LMarker, LTooltip, LPolyline } from 'vue2-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Solucionar problema de iconos en Leaflet con Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default {
    name: 'BusRouteMap',
    components: {
        LMap,
        LTileLayer,
        LMarker,
        LTooltip,
        LPolyline
    },
    props: {
        title: {
            type: String,
            default: 'Visualización de Ruta'
        },
        stops: {
            type: Array,
            required: true,
            default: () => []
        },
        routeId: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            zoom: 10,
            center: [-5.19449, -80.63282],
            tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            showRealRoute: false,
            isLoading: false,
            routeDistance: null,
            routeDuration: null,
            mapReady: false,
            routeCoordinates: [],
            mapOptions: {
                zoomControl: true,
                attributionControl: true,
                worldCopyJump: false
            },

            // Iconos personalizados
            startIcon: L.divIcon({
                className: "custom-start-marker",
                html: '<div style="background-color: #4CAF50; width: 32px; height: 32px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;"><span style="color: white; font-weight: bold;">I</span></div>',
                iconSize: [32, 32],
                iconAnchor: [16, 16],
            }),
            endIcon: L.divIcon({
                className: "custom-end-marker",
                html: '<div style="background-color: #f44336; width: 32px; height: 32px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;"><span style="color: white; font-weight: bold;">F</span></div>',
                iconSize: [32, 32],
                iconAnchor: [16, 16],
            }),
            stopIcon: L.divIcon({
                className: "custom-stop-marker",
                html: '<div style="background-color: #2196F3; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>',
                iconSize: [24, 24],
                iconAnchor: [12, 12],
            })
        };
    },
    computed: {
        validStops() {
            return this.stops.filter(stop => {
                const lat = parseFloat(stop.latitude || stop.StpLatitude);
                const lng = parseFloat(stop.longitude || stop.StpLongitude);

                return (
                    !isNaN(lat) &&
                    !isNaN(lng) &&
                    Math.abs(lat) <= 90 &&
                    Math.abs(lng) <= 180
                );
            }).map(stop => ({
                id: stop.id || stop.StpID || stop.BrsID || Math.random(),
                name: stop.name || stop.StpName || stop.stopName || 'Parada',
                latitude: parseFloat(stop.latitude || stop.StpLatitude),
                longitude: parseFloat(stop.longitude || stop.StpLongitude),
                time: stop.time || stop.BrsEstimatedTime || stop.estimatedTime
            }));
        }
    },
    watch: {
        stops: {
            handler() {
                this.$nextTick(() => {
                    this.initMap();
                });
            },
            immediate: true
        }
    },
    methods: {
        initMap() {
            if (this.validStops.length > 0) {
                const lats = this.validStops.map(s => s.latitude);
                const lngs = this.validStops.map(s => s.longitude);

                const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;
                const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;

                this.center = [avgLat, avgLng];
                
                // Ajustar zoom basado en la distancia entre puntos
                if (this.validStops.length > 1) {
                    const maxLat = Math.max(...lats);
                    const minLat = Math.min(...lats);
                    const latDiff = maxLat - minLat;
                    
                    if (latDiff > 0.5) this.zoom = 8;
                    else if (latDiff > 0.1) this.zoom = 10;
                    else this.zoom = 12;
                }
            }
        },

        onMapReady() {
            this.mapReady = true;
            
            this.$nextTick(() => {
                if (this.$refs.map && this.$refs.map.mapObject) {
                    // Forzar redimensionamiento
                    setTimeout(() => {
                        this.$refs.map.mapObject.invalidateSize();
                        
                        // Ajustar vista a todas las paradas
                        if (this.validStops.length >= 1) {
                            const bounds = L.latLngBounds(
                                this.validStops.map(s => [s.latitude, s.longitude])
                            );
                            this.$refs.map.mapObject.fitBounds(bounds, {
                                padding: [50, 50],
                                maxZoom: 15
                            });
                        }
                    }, 100);
                }
            });
        },

        getStopIcon(index) {
            if (index === 0) return this.startIcon;
            if (index === this.validStops.length - 1) return this.endIcon;
            return this.stopIcon;
        },

        toggleRouteType() {
            if (this.showRealRoute) {
                this.calculateRoute();
            } else {
                this.routeCoordinates = [];
                this.routeDistance = null;
                this.routeDuration = null;
            }
        },

        async calculateRoute() {
            if (this.validStops.length < 2) {
                return;
            }

            this.isLoading = true;
            this.routeCoordinates = [];
            this.routeDistance = null;
            this.routeDuration = null;

            try {
                // Usar API alternativa que no tenga problemas de CORS
                const waypoints = this.validStops.map(stop => 
                    `${stop.longitude},${stop.latitude}`
                ).join(';');


                const url = `https://router.project-osrm.org/route/v1/driving/${waypoints}?overview=full&geometries=geojson`;

                // Usar tu propio proxy o una API alternativa
                 const response = await fetch(url);

                // Alternativa: usar tu propio backend como proxy
                // const response = await fetch(`/api/route?waypoints=${encodeURIComponent(waypoints)}`);

                if (!response.ok) {
                    throw new Error('Error en la respuesta del servidor');
                }

                const data = await response.json();
                
                if (data.routes && data.routes.length > 0) {
                    const route = data.routes[0];
                    this.routeCoordinates = route.geometry.coordinates.map(coord => [coord[1], coord[0]]);
                    this.routeDistance = (route.distance / 1000).toFixed(1);
                    this.routeDuration = this.formatDuration(route.duration);
                }

            } catch (error) {
                console.error('Error calculando ruta:', error);
                // Fallback: usar línea recta pero mostrar advertencia
                this.showRealRoute = false;
                this.$emit('error', 'No se pudo calcular la ruta. Mostrando línea directa.');
            } finally {
                this.isLoading = false;
            }
        },

        formatDuration(seconds) {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);

            if (hours > 0) {
                return `${hours}h ${minutes}min`;
            } else {
                return `${minutes} min`;
            }
        }
    },

    mounted() {
       
    },

    beforeDestroy() {
        // Limpiar recursos
        this.routeCoordinates = [];
    }
};
</script>

<style scoped>
.route-map-container {
    width: 100%;
    height: 100%;
}

.route-map {
    width: 100%;
    height: 450px;
    border-radius: 4px;
    overflow: hidden;
    z-index: 1;
}

/* Asegurar que Leaflet se muestre correctamente */
:deep(.leaflet-container) {
    height: 100%;
    width: 100%;
    z-index: 1;
}

:deep(.leaflet-tooltip) {
    background-color: rgba(255, 255, 255, 0.95);
    border: 2px solid #1976D2;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    font-size: 12px;
    padding: 6px 10px;
    font-weight: 500;
}

:deep(.leaflet-tooltip-top:before) {
    border-top-color: #1976D2;
}
</style>